import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { normalizeApiError } from '@/lib/apiEnvelope'
import { queryClient } from '@/lib/queryClient'

import { notificacoesApi } from '../api/notificacoesApi'
import type { NotificacaoListFilters } from '../types/notificacao'

export const notificacaoKeys = {
  all: ['notificacoes'] as const,
  lists: () => [...notificacaoKeys.all, 'list'] as const,
  list: (filters: NotificacaoListFilters) => [...notificacaoKeys.lists(), filters] as const,
  unreadCount: () => [...notificacaoKeys.all, 'unread-count'] as const,
}

export function useNotificacoes(filters: NotificacaoListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: notificacaoKeys.list(filters),
    queryFn: () => notificacoesApi.list(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  })
}

/** Contador do sino — atualizado por polling (sem WebSocket). */
export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: notificacaoKeys.unreadCount(),
    queryFn: notificacoesApi.contador,
    refetchInterval: 60_000,
  })
}

/** Idempotente — sem toast, só reflete o estado de "lida". */
export function useMarcarNotificacaoLida() {
  return useMutation({
    mutationFn: (id: string) => notificacoesApi.marcarLida(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificacaoKeys.all })
    },
  })
}

export function useMarcarTodasLidas() {
  return useMutation({
    mutationFn: () => notificacoesApi.marcarTodasLidas(),
    onSuccess: (quantidade) => {
      void queryClient.invalidateQueries({ queryKey: notificacaoKeys.all })
      toast.success(
        quantidade > 0
          ? `${quantidade} notificaç${quantidade === 1 ? 'ão marcada' : 'ões marcadas'} como lida${quantidade === 1 ? '' : 's'}.`
          : 'Nenhuma notificação pendente.',
      )
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}
